import { useState, type FormEvent } from 'react';
import { useAuth } from '@toki/auth';
import { requestPortalLink } from '../services/api';
import { tenantConfig } from '../tenant';
import styles from './Login.module.css';

/**
 * LA PUERTA. Una sola pantalla para todo el mundo, y cada uno acaba donde le
 * toca por su rol (Jose, 2026-09-25): el técnico entra al panel con su
 * contraseña; quien pide soporte recibe un enlace y aterriza en el portal.
 *
 * ⚠️ UNA sola pantalla, pero NO una sola puerta: por detrás siguen siendo dos
 * caminos con reglas distintas, y eso no es una complicación heredada sino lo
 * que sostiene el paso 84.
 *
 *   · `/auth/login` autentica **solo a staff**. Un `requester` con la
 *     contraseña correcta recibe el mismo 401 que quien se equivoca.
 *   · `/auth/portal/link` contesta **siempre lo mismo** — la dirección tiene
 *     cuenta, no la tiene, no existe o es de staff: mismo texto.
 *
 * ⚠️ Y la regla que hace que unificar la pantalla no rompa nada: **la pantalla
 * NO elige la vía por ti**. La persona pulsa una pestaña. Si el formulario
 * cambiara según el correo tecleado —contraseña para unos, enlace para
 * otros—, esta pantalla sería un **oráculo de rol**: probando direcciones
 * cualquiera averiguaría quién trabaja en Adaptia y quién es cliente, que es
 * justo lo que el 401 único del paso 84 existe para impedir. El diseño de
 * Jose ya lo resuelve así, con dos pestañas manuales.
 *
 * Lo que el documento de diseño dibuja aquí y NO se ha construido está en K29:
 * «Continuar con cuenta de empresa» (no hay SSO de Microsoft), «¿La has
 * olvidado?» (no hay contraseñas que recuperar en el portal, ni recuperación
 * en el admin), «Mantener la sesión en este equipo» y «Estado de los
 * servicios». No se maquetan: una puerta que ofrece lo que no puede cumplir
 * es peor que una puerta sobria.
 */
type Via = 'password' | 'link';

export function AdminLogin() {
  const { provider } = useAuth();
  const [via, setVia] = useState<Via>('password');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const handlePassword = async (e: FormEvent) => {
    e.preventDefault();
    if (!provider.loginWithEmailPassword) return;
    setError(null);
    setLoading(true);
    try {
      // No hace falta avisar a nadie: el provider notifica el cambio de
      // estado y `AdminShell` vuelve a pintar con la sesión ya abierta.
      await provider.loginWithEmailPassword(email, password);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error de autenticación');
    } finally {
      setLoading(false);
    }
  };

  const handleLink = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      // ⚠️ El texto lo escribe el BACKEND y se pinta tal cual. Es el mismo
      // exista la cuenta o no; si esta pantalla lo interpretara («no
      // encontramos esa dirección»), la puerta pasaría a ser un listado de
      // con quién trabaja Adaptia. Hay un test que compara las respuestas
      // enteras.
      const res = await requestPortalLink(email);
      setNotice(res.message);
    } catch {
      // Tampoco aquí se distingue: un fallo de red y una dirección sin cuenta
      // dicen lo mismo.
      setNotice('Si esa dirección tiene acceso, te llegará un enlace para entrar.');
    } finally {
      setLoading(false);
    }
  };

  const switchVia = (v: Via) => {
    setVia(v);
    setError(null);
    setNotice(null);
  };

  return (
    <div className={styles.container}>
      <div className={styles.frame}>
        {/* ── Columna del formulario ───────────────────────────── */}
        <div className={styles.pane}>
          <p className={styles.eyebrow}>Portal de soporte IT</p>
          <h1 className={styles.title}>Accede a tu soporte</h1>
          <p className={styles.lede}>
            Consulta tus tickets y abre nuevas peticiones al equipo de IT.
          </p>

          {/* Las dos vías, y las elige la PERSONA. Ver el aviso del oráculo
              arriba: esto no puede pasar a decidirse por el correo. */}
          <div className={styles.tabs} role="tablist" aria-label="Forma de acceso">
            <button
              type="button"
              role="tab"
              aria-selected={via === 'password'}
              className={`${styles.tab} ${via === 'password' ? styles.tabOn : ''}`}
              onClick={() => switchVia('password')}
            >
              Contraseña
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={via === 'link'}
              className={`${styles.tab} ${via === 'link' ? styles.tabOn : ''}`}
              onClick={() => switchVia('link')}
            >
              Enlace por correo
            </button>
          </div>

          {via === 'password' ? (
            <form className={styles.form} onSubmit={handlePassword}>
              <label className={styles.label}>
                Correo de trabajo
                <input
                  className={styles.input}
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="nombre@empresa.es"
                  required
                  autoComplete="email"
                />
              </label>

              <label className={styles.label}>
                Contraseña
                <input
                  className={styles.input}
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoComplete="current-password"
                />
              </label>

              {error && <p className={styles.error} role="alert">{error}</p>}

              <button className={styles.button} type="submit" disabled={loading}>
                {loading ? 'Entrando…' : 'Entrar'}
              </button>
            </form>
          ) : (
            <form className={styles.form} onSubmit={handleLink}>
              <p className={styles.lede}>
                Te enviamos un enlace a tu correo. Sin contraseñas.
              </p>
              <label className={styles.label}>
                Correo de trabajo
                <input
                  className={styles.input}
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="nombre@empresa.es"
                  required
                  autoComplete="email"
                />
              </label>

              {notice && <p className={styles.notice} role="status">{notice}</p>}

              <button className={styles.button} type="submit" disabled={loading}>
                {loading ? 'Enviando…' : 'Enviar enlace de acceso'}
              </button>
            </form>
          )}

          {/* El pie del diseño, con una diferencia: NO hay «regístrate». Las
              cuentas las da de alta Adaptia (K12 borró `/auth/register`). */}
          <p className={styles.foot}>
            ¿No tienes acceso? Pídeselo a tu responsable o escribe a{' '}
            <a href="mailto:soporte@adaptiaservices.com">soporte@adaptiaservices.com</a>.
          </p>
        </div>

        {/* ── Columna oscura ───────────────────────────────────────
            El documento pone aquí «Estado de los servicios», que no existe
            (K29): no hay ni monitorización ni página de estado, y cuatro
            filas verdes fijas serían una promesa falsa — justo en la pantalla
            donde alguien llega **porque algo no le funciona**. Se queda el
            panel con lo que sí es verdad y se puede cumplir: cómo llegar a un
            humano. */}
        <aside className={styles.side}>
          {/* La marca vive AQUÍ, sobre la tinta (Jose, 2026-09-25). Va la
              variante `logoOnDark` del tenant: el wordmark de color sobre
              #001D33 se apaga y el de tinta sería invisible. */}
          <img
            className={styles.sideLogo}
            src={tenantConfig.logoOnDark}
            alt={tenantConfig.logoAlt}
          />

          <div className={styles.sideBody}>
          <div>
            <p className={styles.sideTitle}>Soporte IT de Adaptia</p>
            <p className={styles.sideText}>
              Abre un ticket, sigue su estado y responde a tu técnico desde el mismo
              sitio. Si escribes a soporte por correo, la conversación aparece aquí.
            </p>
          </div>
          <div className={styles.sideRule} />
          <div>
            <p className={styles.sideTitle}>¿Urgencia y no puedes entrar?</p>
            <p className={styles.sideText}>
              Escribe a{' '}
              <a href="mailto:soporte@adaptiaservices.com">soporte@adaptiaservices.com</a>{' '}
              y te abrimos el ticket nosotros.
            </p>
          </div>
          </div>
        </aside>
      </div>
    </div>
  );
}
